import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { compressImage } from "@/lib/imageCompress";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  ShieldCheck,
  UserPlus,
  Ban,
  CheckCircle2,
  Trash2,
  Crown,
  Users,
  PhoneCall,
  ClipboardList,
  UserSquare2,
  Wallet,
  QrCode,
  Upload,
  CreditCard,
  Stethoscope,
} from "lucide-react";

const PLAN_OPTIONS = [
  { key: "starter", label: "Starter" },
  { key: "growth", label: "Growth" },
  { key: "business", label: "Business" },
  { key: "enterprise", label: "Enterprise" },
];

const IST_DATE = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      timeZone: "Asia/Kolkata",
    });
  } catch {
    return "—";
  }
};

export default function Admins() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [creating, setCreating] = useState(false);

  const [confirm, setConfirm] = useState(null); // {kind:"delete"|"disable"|"enable", admin}

  // Wallet settings — the platform-wide price-per-generated-image and the
  // super admin's own payment QR code, shown to shop owners so they know
  // how much to pay and where to scan.
  const [walletSettings, setWalletSettings] = useState({ price_per_image: 0, qr_code: null });
  const [walletPriceInput, setWalletPriceInput] = useState("0");
  const [savingWalletSettings, setSavingWalletSettings] = useState(false);

  // Per-admin manual wallet recharge dialog.
  const [rechargeAdmin, setRechargeAdmin] = useState(null);
  const [rechargeAmount, setRechargeAmount] = useState("");
  const [recharging, setRecharging] = useState(false);

  // Per-admin plan-change dialog.
  const [planAdmin, setPlanAdmin] = useState(null);
  const [planKey, setPlanKey] = useState("starter");
  const [savingPlan, setSavingPlan] = useState(false);

  // Per-admin healthcare-module toggle — only this page can flip it, per
  // the backend gate (super-admin only).
  const [togglingHealthcare, setTogglingHealthcare] = useState(null); // admin id currently in flight

  // Validity editor state — mirrors the Settings-page dialog so super-admin
  // can also set an admin's expiry directly from this Admins list.
  const [validityAdmin, setValidityAdmin] = useState(null);
  const [validityDate, setValidityDate] = useState("");
  const [validitySaving, setValiditySaving] = useState(false);

  const openValidity = (a) => {
    setValidityAdmin(a);
    setValidityDate(
      a.validity_expires_at ? String(a.validity_expires_at).slice(0, 10) : "",
    );
  };
  const closeValidity = () => {
    setValidityAdmin(null);
    setValidityDate("");
  };
  const quickExtend = (days) => {
    const base = validityDate ? new Date(validityDate) : new Date();
    if (isNaN(base.getTime())) return;
    base.setDate(base.getDate() + days);
    setValidityDate(base.toISOString().slice(0, 10));
  };
  const saveValidity = async (date) => {
    if (!validityAdmin) return;
    setValiditySaving(true);
    try {
      await api.patch(`/auth/admins/${validityAdmin.id}/validity`, {
        validity_expires_at: date || null,
      });
      toast.success(date ? `Validity set to ${date}` : "Validity cleared");
      closeValidity();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update validity");
    } finally {
      setValiditySaving(false);
    }
  };

  useEffect(() => {
    if (user && !user.is_super_admin) {
      nav("/dashboard", { replace: true });
    }
  }, [user, nav]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/auth/admins");
      setAdmins(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load admins");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadWalletSettings = useCallback(async () => {
    try {
      const { data } = await api.get("/super/wallet-settings");
      setWalletSettings(data);
      setWalletPriceInput(String(data.price_per_image ?? 0));
    } catch (e) {
      /* non-fatal — the wallet card just starts blank */
    }
  }, []);

  useEffect(() => {
    load();
    loadWalletSettings();
  }, [load, loadWalletSettings]);

  const uploadWalletQr = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      toast.error("Image must be under 15 MB");
      return;
    }
    try {
      const r = await compressImage(file, { maxDimension: 800 });
      setWalletSettings((w) => ({ ...w, qr_code: r.dataUrl }));
      toast.success("QR code loaded — click Save to apply");
    } catch {
      toast.error("Could not read the image. Try again.");
    }
  };

  const saveWalletSettings = async () => {
    const price = Number(walletPriceInput);
    if (isNaN(price) || price < 0) {
      toast.error("Enter a valid price (0 or more)");
      return;
    }
    setSavingWalletSettings(true);
    try {
      const { data } = await api.put("/super/wallet-settings", {
        price_per_image: price,
        qr_code: walletSettings.qr_code,
      });
      setWalletSettings(data);
      toast.success("Wallet settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save wallet settings");
    } finally {
      setSavingWalletSettings(false);
    }
  };

  const openRecharge = (a) => {
    setRechargeAdmin(a);
    setRechargeAmount("");
  };
  const closeRecharge = () => {
    setRechargeAdmin(null);
    setRechargeAmount("");
  };
  const submitRecharge = async () => {
    const amt = Number(rechargeAmount);
    if (!rechargeAdmin || isNaN(amt) || amt <= 0) {
      toast.error("Enter an amount greater than zero");
      return;
    }
    setRecharging(true);
    try {
      const { data } = await api.post(
        `/auth/admins/${rechargeAdmin.id}/wallet/recharge`,
        { amount: amt },
      );
      toast.success(
        `₹${amt} credited to ${rechargeAdmin.name} — new balance ₹${Number(data.wallet_balance).toFixed(2)}`,
      );
      closeRecharge();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not recharge wallet");
    } finally {
      setRecharging(false);
    }
  };

  const openPlan = (a) => {
    setPlanAdmin(a);
    setPlanKey(a.plan?.plan_key || "starter");
  };
  const closePlan = () => setPlanAdmin(null);
  const submitPlan = async () => {
    if (!planAdmin) return;
    setSavingPlan(true);
    try {
      await api.patch(`/auth/admins/${planAdmin.id}/plan`, { plan_key: planKey });
      toast.success(`${planAdmin.name} moved to ${PLAN_OPTIONS.find((p) => p.key === planKey)?.label || planKey}`);
      closePlan();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not change plan");
    } finally {
      setSavingPlan(false);
    }
  };

  const toggleHealthcareModule = async (admin, enabled) => {
    setTogglingHealthcare(admin.id);
    // Optimistic — flip it locally first so the switch feels instant, then
    // reconcile with the server response (or roll back on failure).
    setAdmins((prev) =>
      prev.map((a) => (a.id === admin.id ? { ...a, healthcare_module_enabled: enabled } : a)),
    );
    try {
      await api.patch(`/auth/admins/${admin.id}/healthcare-module`, { enabled });
      toast.success(`Healthcare module ${enabled ? "enabled" : "disabled"} for ${admin.name}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update the healthcare module");
      setAdmins((prev) =>
        prev.map((a) => (a.id === admin.id ? { ...a, healthcare_module_enabled: !enabled } : a)),
      );
    } finally {
      setTogglingHealthcare(null);
    }
  };

  const submitCreate = async () => {
    const name = form.name.trim();
    const email = form.email.trim().toLowerCase();
    const pw = form.password.trim();
    if (name.length < 2) return toast.error("Name is required.");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
      return toast.error("Enter a valid email.");
    if (pw.length < 8)
      return toast.error("Password must be at least 8 characters.");

    setCreating(true);
    try {
      await api.post("/auth/admins", {
        name,
        email,
        password: pw,
        phone: form.phone.trim(),
      });
      toast.success(`Admin "${name}" created`);
      setForm({ name: "", email: "", phone: "", password: "" });
      setCreateOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create admin");
    } finally {
      setCreating(false);
    }
  };

  const doAction = async () => {
    if (!confirm) return;
    const { kind, admin } = confirm;
    setBusyId(admin.id);
    try {
      if (kind === "disable") {
        await api.post(`/auth/admins/${admin.id}/disable`);
        toast.success(`${admin.name} disabled`);
      } else if (kind === "enable") {
        await api.post(`/auth/admins/${admin.id}/enable`);
        toast.success(`${admin.name} enabled`);
      } else if (kind === "delete") {
        await api.delete(`/auth/admins/${admin.id}`);
        toast.success(`${admin.name} deleted with all their data`);
      }
      setConfirm(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="admins-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold flex items-center gap-2">
            <ShieldCheck className="w-6 h-6 text-[#0055FF]" />
            Admins
          </h1>
          <p className="text-slate-500 mt-1 text-sm">
            Manage every tenant on the platform. Each admin has their own
            employees, customers, calls and tasks — invisible to other admins.
            Disable to block login while keeping their data.
          </p>
        </div>
        <Button
          onClick={() => setCreateOpen(true)}
          className="bg-[#0055FF] hover:bg-[#0040CC]"
          data-testid="admins-new"
        >
          <UserPlus className="w-4 h-4 mr-1" />
          Add admin
        </Button>
      </div>

      <Card className="shadow-none border-slate-200" data-testid="wallet-settings-card">
        <CardHeader className="border-b bg-slate-50/50">
          <CardTitle className="font-heading text-base flex items-center gap-2">
            <Wallet className="w-4 h-4 text-slate-500" />
            Wallet settings
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          <p className="text-sm text-slate-500 mb-4">
            Set what each AI-generated product photo costs and upload your
            payment QR code. Shop owners see both on their Marketplace page,
            pay you directly (UPI scan), and you credit their wallet from the
            &quot;Recharge&quot; button on their row below.
          </p>
          <div className="flex flex-col sm:flex-row gap-6">
            <div className="space-y-1.5 sm:w-56">
              <Label htmlFor="wallet-price-input">Price per image (₹)</Label>
              <Input
                id="wallet-price-input"
                type="number"
                min="0"
                step="0.01"
                value={walletPriceInput}
                onChange={(e) => setWalletPriceInput(e.target.value)}
                data-testid="wallet-price-input"
              />
              <p className="text-xs text-slate-400">
                Set to 0 to disable wallet billing entirely.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label>Payment QR code</Label>
              <div className="flex items-center gap-3">
                {walletSettings.qr_code ? (
                  <img
                    src={walletSettings.qr_code}
                    alt="Payment QR code"
                    className="w-20 h-20 object-contain rounded border border-slate-200"
                    data-testid="wallet-qr-preview"
                  />
                ) : (
                  <div className="w-20 h-20 rounded border border-dashed border-slate-300 flex items-center justify-center text-slate-300">
                    <QrCode className="w-8 h-8" />
                  </div>
                )}
                <label className="inline-flex">
                  <span className="cursor-pointer inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-md border border-slate-200 hover:border-slate-300 hover:bg-slate-50">
                    <Upload className="w-3.5 h-3.5" />
                    {walletSettings.qr_code ? "Replace" : "Upload"}
                  </span>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={uploadWalletQr}
                    data-testid="wallet-qr-upload"
                  />
                </label>
              </div>
            </div>
          </div>
          <Button
            onClick={saveWalletSettings}
            disabled={savingWalletSettings}
            className="bg-[#0055FF] hover:bg-[#0040CC] mt-4"
            data-testid="wallet-settings-save"
          >
            {savingWalletSettings ? "Saving…" : "Save wallet settings"}
          </Button>
        </CardContent>
      </Card>

      <Card className="shadow-none border-slate-200 overflow-hidden">
        <CardHeader className="border-b bg-slate-50/50">
          <CardTitle className="font-heading text-base flex items-center gap-2">
            <Users className="w-4 h-4 text-slate-500" />
            All admins ({admins.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm" data-testid="admins-table">
            <thead className="bg-slate-50 mono text-[10px] uppercase tracking-widest text-slate-500">
              <tr>
                <th className="text-left px-4 py-3">Admin</th>
                <th className="text-left px-4 py-3">Phone</th>
                <th className="text-left px-4 py-3">Tenant</th>
                <th className="text-left px-4 py-3">Created</th>
                <th className="text-left px-4 py-3">Plan</th>
                <th className="text-left px-4 py-3">Validity</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Wallet</th>
                <th className="text-left px-4 py-3">Healthcare</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {loading && (
                <tr>
                  <td colSpan={10} className="text-center py-10 text-slate-500">
                    Loading…
                  </td>
                </tr>
              )}
              {!loading && admins.length === 0 && (
                <tr>
                  <td colSpan={10} className="text-center py-10 text-slate-500">
                    No admins yet. Click &quot;Add admin&quot; to create one.
                  </td>
                </tr>
              )}
              {!loading &&
                admins.map((a) => {
                  const disabled = a.status === "disabled";
                  return (
                    <tr
                      key={a.id}
                      data-testid={`admin-row-${a.id}`}
                      className={disabled ? "bg-slate-50/40" : ""}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="font-medium text-sm">{a.name}</div>
                          {a.is_super_admin && (
                            <span
                              className="inline-flex items-center gap-1 mono text-[9px] uppercase text-amber-800 bg-amber-100 border border-amber-200 rounded px-1.5 py-0.5"
                              data-testid={`admin-super-${a.id}`}
                            >
                              <Crown className="w-3 h-3" />
                              super
                            </span>
                          )}
                          {a.is_you && !a.is_super_admin && (
                            <span className="mono text-[9px] uppercase text-slate-600 bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5">
                              you
                            </span>
                          )}
                        </div>
                        <div className="mono text-[10px] text-slate-500">
                          {a.email}
                        </div>
                      </td>
                      <td className="px-4 py-3 mono text-xs text-slate-700">
                        {a.phone || "—"}
                      </td>
                      <td className="px-4 py-3 mono text-[11px] text-slate-600">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="inline-flex items-center gap-1">
                            <Users className="w-3 h-3" /> {a.employee_count}
                          </span>
                          <span className="inline-flex items-center gap-1">
                            <UserSquare2 className="w-3 h-3" /> {a.customer_count}
                          </span>
                          <span className="inline-flex items-center gap-1">
                            <ClipboardList className="w-3 h-3" /> {a.task_count}
                          </span>
                          <span className="inline-flex items-center gap-1">
                            <PhoneCall className="w-3 h-3" /> {a.call_count}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 mono text-xs text-slate-600">
                        {IST_DATE(a.created_at)}
                      </td>
                      <td className="px-4 py-3">
                        {a.is_super_admin ? (
                          <span className="text-slate-400">—</span>
                        ) : a.plan ? (
                          <button
                            type="button"
                            onClick={() => openPlan(a)}
                            className="text-left group"
                            title="Click to change plan"
                            data-testid={`admin-plan-cell-${a.id}`}
                          >
                            <div className="text-[11px] font-semibold text-slate-700 group-hover:text-[var(--shop-primary,#0055FF)] transition">
                              {a.plan.plan_name}
                            </div>
                            <div className="mono text-[10px] text-slate-500">
                              {a.plan.status === "trialing"
                                ? `Trial · ${a.plan.days_left ?? 0}d left`
                                : a.plan.status === "active"
                                  ? `Active · ${a.plan.days_left ?? 0}d`
                                  : a.plan.status}
                            </div>
                          </button>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {a.is_super_admin ? (
                          <span className="text-slate-400 text-[11px] mono">never</span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => openValidity(a)}
                            className="text-left group"
                            data-testid={`admin-validity-cell-${a.id}`}
                            title="Click to change validity"
                          >
                            {a.validity_expires_at ? (
                              <>
                                <div className={
                                  "mono text-[11px] " +
                                  (a.days_until_expiry != null && a.days_until_expiry <= 7
                                    ? "text-amber-700 font-semibold"
                                    : "text-slate-700")
                                }>
                                  {String(a.validity_expires_at).slice(0, 10)}
                                </div>
                                <div className="text-[10px] text-slate-500 group-hover:text-[var(--shop-primary,#0055FF)] transition">
                                  {a.days_until_expiry != null && a.days_until_expiry >= 0
                                    ? `in ${a.days_until_expiry} day${a.days_until_expiry === 1 ? "" : "s"} · edit`
                                    : "expired · click to renew"}
                                </div>
                              </>
                            ) : (
                              <span className="text-[11px] text-slate-500 underline decoration-dotted underline-offset-2 hover:text-[var(--shop-primary,#0055FF)]">
                                + set expiry
                              </span>
                            )}
                          </button>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {disabled ? (
                          <span
                            className="inline-flex items-center gap-1 mono text-[10px] uppercase text-red-700 bg-red-50 border border-red-200 rounded px-2 py-0.5"
                            data-testid={`admin-status-${a.id}`}
                          >
                            <Ban className="w-3 h-3" />
                            disabled
                          </span>
                        ) : (
                          <span
                            className="inline-flex items-center gap-1 mono text-[10px] uppercase text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-0.5"
                            data-testid={`admin-status-${a.id}`}
                          >
                            <CheckCircle2 className="w-3 h-3" />
                            active
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 mono text-xs">
                        {a.is_super_admin ? (
                          <span className="text-slate-400">—</span>
                        ) : (
                          <span
                            className={
                              a.wallet_balance > 0
                                ? "text-emerald-700 font-semibold"
                                : "text-slate-400"
                            }
                            data-testid={`admin-wallet-balance-${a.id}`}
                          >
                            ₹{Number(a.wallet_balance || 0).toFixed(2)}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {a.is_super_admin ? (
                          <span className="text-slate-400">—</span>
                        ) : (
                          <div className="flex items-center gap-2" title="Doctor booking + pathology services">
                            <Stethoscope
                              className={`w-3.5 h-3.5 shrink-0 ${
                                a.healthcare_module_enabled ? "text-emerald-600" : "text-slate-300"
                              }`}
                            />
                            <Switch
                              checked={!!a.healthcare_module_enabled}
                              disabled={togglingHealthcare === a.id}
                              onCheckedChange={(v) => toggleHealthcareModule(a, v)}
                              data-testid={`admin-healthcare-toggle-${a.id}`}
                            />
                            <span
                              className={`mono text-[10px] uppercase ${
                                a.healthcare_module_enabled ? "text-emerald-700" : "text-slate-400"
                              }`}
                            >
                              {a.healthcare_module_enabled ? "On" : "Off"}
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-1.5">
                          {!a.is_super_admin && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openRecharge(a)}
                              className="text-[#0055FF] border-blue-200 hover:bg-blue-50"
                              data-testid={`admin-recharge-${a.id}`}
                            >
                              <Wallet className="w-3.5 h-3.5 mr-1" />
                              Recharge
                            </Button>
                          )}
                          {a.is_super_admin && a.is_you ? (
                            <span className="mono text-[10px] text-slate-400 italic">
                              protected
                            </span>
                          ) : (
                            <>
                              {disabled ? (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  disabled={busyId === a.id}
                                  onClick={() =>
                                    setConfirm({ kind: "enable", admin: a })
                                  }
                                  className="text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                                  data-testid={`admin-enable-${a.id}`}
                                >
                                  <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
                                  Enable
                                </Button>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  disabled={busyId === a.id || a.is_you}
                                  onClick={() =>
                                    setConfirm({ kind: "disable", admin: a })
                                  }
                                  className="text-amber-700 border-amber-200 hover:bg-amber-50"
                                  data-testid={`admin-disable-${a.id}`}
                                >
                                  <Ban className="w-3.5 h-3.5 mr-1" />
                                  Disable
                                </Button>
                              )}
                              {/* Deleting a super admin (even one that isn't
                                  you) stays blocked server-side — a second
                                  super-admin row should be disabled, not
                                  wiped along with its tenant's data. */}
                              {!a.is_super_admin && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  disabled={busyId === a.id || a.is_you}
                                  onClick={() =>
                                    setConfirm({ kind: "delete", admin: a })
                                  }
                                  className="text-red-700 border-red-200 hover:bg-red-50"
                                  data-testid={`admin-delete-${a.id}`}
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Create admin dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-md" data-testid="admins-create-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Create new admin</DialogTitle>
            <DialogDescription>
              This admin gets a completely separate tenant. They cannot see —
              nor be seen by — other admins. You set the initial password; they
              can change it after logging in.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <label className="block space-y-1">
              <div className="mono text-[10px] uppercase tracking-widest text-slate-500">
                Full name
              </div>
              <Input
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Priya Sharma"
                data-testid="admins-form-name"
              />
            </label>
            <label className="block space-y-1">
              <div className="mono text-[10px] uppercase tracking-widest text-slate-500">
                Email
              </div>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                placeholder="admin@company.com"
                data-testid="admins-form-email"
              />
            </label>
            <label className="block space-y-1">
              <div className="mono text-[10px] uppercase tracking-widest text-slate-500">
                Phone (optional)
              </div>
              <Input
                value={form.phone}
                onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                placeholder="+91..."
                data-testid="admins-form-phone"
              />
            </label>
            <label className="block space-y-1">
              <div className="mono text-[10px] uppercase tracking-widest text-slate-500">
                Initial password (min 8 chars)
              </div>
              <Input
                type="text"
                value={form.password}
                onChange={(e) =>
                  setForm((f) => ({ ...f, password: e.target.value }))
                }
                placeholder="Set a strong password"
                data-testid="admins-form-password"
              />
            </label>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              disabled={creating}
              onClick={() => setCreateOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={submitCreate}
              disabled={creating}
              className="bg-[#0055FF] hover:bg-[#0040CC]"
              data-testid="admins-form-submit"
            >
              {creating ? "Creating…" : "Create admin"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm action dialog */}
      <Dialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <DialogContent className="sm:max-w-md" data-testid="admins-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {confirm?.kind === "delete" && "Delete this admin?"}
              {confirm?.kind === "disable" && "Disable this admin?"}
              {confirm?.kind === "enable" && "Enable this admin?"}
            </DialogTitle>
            <DialogDescription>
              {confirm?.kind === "delete" && (
                <>
                  This is permanent. It removes{" "}
                  <b>{confirm?.admin?.name}</b> and every piece of data in their
                  tenant — employees, customers, tasks, calls, attendance,
                  configs. There&apos;s no undo.
                </>
              )}
              {confirm?.kind === "disable" && (
                <>
                  <b>{confirm?.admin?.name}</b> will not be able to log in until
                  you re-enable them. All their data stays intact.
                </>
              )}
              {confirm?.kind === "enable" && (
                <>Re-enables login for <b>{confirm?.admin?.name}</b>.</>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              disabled={!!busyId}
              onClick={() => setConfirm(null)}
            >
              Cancel
            </Button>
            <Button
              onClick={doAction}
              disabled={!!busyId}
              className={
                confirm?.kind === "delete"
                  ? "bg-red-600 hover:bg-red-700 text-white"
                  : confirm?.kind === "disable"
                    ? "bg-amber-600 hover:bg-amber-700 text-white"
                    : "bg-emerald-600 hover:bg-emerald-700 text-white"
              }
              data-testid="admins-confirm-submit"
            >
              {busyId
                ? "Working…"
                : confirm?.kind === "delete"
                  ? "Delete forever"
                  : confirm?.kind === "disable"
                    ? "Disable"
                    : "Enable"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Validity dialog — reusable from any row's "set expiry" / date link. */}
      <Dialog open={!!validityAdmin} onOpenChange={(v) => { if (!v) closeValidity(); }}>
        <DialogContent className="max-w-sm" data-testid="admin-validity-dialog">
          <DialogHeader>
            <DialogTitle>Set validity</DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              {validityAdmin?.name || validityAdmin?.email} will be
              auto-disabled at the end of this date. Leave blank for no expiry.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="admins-validity-date">Expires on</Label>
              <Input
                id="admins-validity-date"
                type="date"
                value={validityDate}
                onChange={(e) => setValidityDate(e.target.value)}
                min={new Date().toISOString().slice(0, 10)}
                data-testid="admin-validity-date-input"
              />
            </div>
            <div className="flex flex-wrap gap-1.5">
              {[30, 90, 180, 365].map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => quickExtend(d)}
                  className="text-xs px-2.5 py-1 rounded-full bg-slate-100 hover:bg-slate-200"
                  data-testid={`admin-validity-plus${d}`}
                >
                  +{d === 365 ? "1 year" : `${d} days`}
                </button>
              ))}
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => saveValidity("")}
              disabled={validitySaving}
              className="text-red-600 hover:text-red-700"
              data-testid="admin-validity-clear"
            >
              Remove expiry
            </Button>
            <Button
              type="button"
              onClick={() => saveValidity(validityDate)}
              disabled={validitySaving || !validityDate}
              className="bg-[var(--shop-primary,#0055FF)] hover:bg-[var(--shop-primary,#0040CC)]"
              data-testid="admin-validity-save"
            >
              {validitySaving ? "Saving…" : "Save validity"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Recharge wallet dialog */}
      <Dialog open={!!rechargeAdmin} onOpenChange={(v) => { if (!v) closeRecharge(); }}>
        <DialogContent className="sm:max-w-sm" data-testid="admin-recharge-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Recharge wallet</DialogTitle>
            <DialogDescription>
              Credit <b>{rechargeAdmin?.name}</b>&apos;s wallet after confirming
              you received their payment. Current balance: ₹
              {Number(rechargeAdmin?.wallet_balance || 0).toFixed(2)}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {walletSettings.qr_code && (
              <div className="flex flex-col items-center gap-2 text-center">
                <img
                  src={walletSettings.qr_code}
                  alt="Payment QR code"
                  className="w-36 h-36 object-contain rounded border border-slate-200"
                />
                {walletSettings.price_per_image > 0 && (
                  <p className="text-xs text-slate-500">
                    ₹{Number(walletSettings.price_per_image).toFixed(2)} per image
                  </p>
                )}
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="recharge-amount">Amount received (₹)</Label>
              <Input
                id="recharge-amount"
                type="number"
                min="0"
                step="0.01"
                value={rechargeAmount}
                onChange={(e) => setRechargeAmount(e.target.value)}
                placeholder="e.g. 500"
                data-testid="recharge-amount-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeRecharge} disabled={recharging}>
              Cancel
            </Button>
            <Button
              onClick={submitRecharge}
              disabled={recharging || !rechargeAmount}
              className="bg-[#0055FF] hover:bg-[#0040CC]"
              data-testid="recharge-submit"
            >
              {recharging ? "Crediting…" : "Credit wallet"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change plan dialog */}
      <Dialog open={!!planAdmin} onOpenChange={(v) => { if (!v) closePlan(); }}>
        <DialogContent className="sm:max-w-sm" data-testid="admin-plan-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <CreditCard className="w-4 h-4 text-slate-500" />
              Change plan
            </DialogTitle>
            <DialogDescription>
              Move <b>{planAdmin?.name}</b> onto a different plan. This marks
              it active immediately with no expiry — it stays in effect until
              you change it again.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2 space-y-1.5">
            <Label htmlFor="plan-select">Plan</Label>
            <Select value={planKey} onValueChange={setPlanKey}>
              <SelectTrigger id="plan-select" data-testid="admin-plan-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PLAN_OPTIONS.map((p) => (
                  <SelectItem key={p.key} value={p.key}>
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closePlan} disabled={savingPlan}>
              Cancel
            </Button>
            <Button
              onClick={submitPlan}
              disabled={savingPlan}
              className="bg-[#0055FF] hover:bg-[#0040CC]"
              data-testid="admin-plan-save"
            >
              {savingPlan ? "Saving…" : "Save plan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
